import { getCareSourceAttribution } from './careSourceAttribution';
import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import {
  KNOWLEDGE_ITEMS_COLLECTION,
  buildKnowledgeItemPayload,
} from '../knowledgeItems/knowledgeItemModel';
import { buildKnowledgeEvidenceDocumentId } from '../knowledgeItems/knowledgeEvidenceIdentity';

const SOURCE_NOTES_COLLECTION = 'careSources';
const PROFILE_SUGGESTION_EXTRACTION_VERSION = 'profile-source-suggestions-v1';
const KNOWN_CONFIDENCE_VALUES = new Set(['high', 'medium', 'low']);

const PROFILE_FIELD_KNOWLEDGE_TYPES = Object.freeze({
  about: 'profile',
  earlyWarningSigns: 'baseline',
  thingsThatUsuallyHelp: 'strategy',
  thingsToAvoid: 'strategy',
  safetyConsiderations: 'safety',
  communicationStyle: 'profile',
  communicationNotes: 'support',
  familySupportTips: 'instruction',
  schoolTherapySuggestions: 'support',
});

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const normalizeConfidence = (value = '') => {
  const confidence = normalizeText(value).toLowerCase();
  return KNOWN_CONFIDENCE_VALUES.has(confidence) ? confidence : 'low';
};

const normalizeSuggestionValues = (value) => {
  const seen = new Set();
  return (Array.isArray(value) ? value : [value])
    .map(normalizeText)
    .filter((item) => {
      const key = item.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getSourceText = (source = {}) => normalizeText(
  source.rawText
  || source.originalText
  || source.sourceText
  || source.cleanedText
);

const getSourceTitle = (source = {}) => normalizeText(
  source.sourceMetadata?.fileName
  || source.metadata?.source?.fileName
  || source.fileName
  || source.title
  || 'Profile source'
);

const getSourceType = (source = {}) => normalizeText(
  source.sourceType
  || source.sourceMetadata?.sourceType
  || source.metadata?.source?.sourceType
  || 'profile_source'
);

export const buildKnowledgeFactsFromProfileSuggestions = ({
  childId = '',
  sourceId = '',
  source = {},
  suggestedProfileUpdates = {},
  userId = null,
  timestamp = null,
} = {}) => {
  const cleanChildId = normalizeText(childId);
  const cleanSourceId = normalizeText(sourceId || source.id);
  const confidence = normalizeConfidence(
    source.profileSuggestionSummary?.confidence || source.confidence
  );
  const sourceText = getSourceText(source);
  const sourceContext = normalizeText(source.sourceContext || source.metadata?.source?.sourceContext || 'profile');
  const capturedBy = {
    id: userId || null,
    sourceDocumentId: cleanSourceId,
    sourceContext,
    confidence,
  };
  const sourceRef = {
    ...getCareSourceAttribution(source),
    sourceId: cleanSourceId,
    sourceVersion: source.staffOriginalVersion ? (source.sourceLifecycle?.version || 'initial') : 'initial',
    sourceTitle: getSourceTitle(source),
    sourceType: getSourceType(source),
    sourceContext,
    sourceCollection: SOURCE_NOTES_COLLECTION,
    sourceText,
    confidence,
    confirmedAt: timestamp,
    confirmedBy: userId || null,
  };

  return Object.entries(PROFILE_FIELD_KNOWLEDGE_TYPES).flatMap(([profileField, knowledgeType]) => (
    normalizeSuggestionValues(suggestedProfileUpdates?.[profileField]).map((value, sourceItemIndex) => ({
      ...buildKnowledgeItemPayload({
        childId: cleanChildId,
        captureId: normalizeText(source.captureId || cleanSourceId),
        title: value,
        summary: value,
        knowledgeType,
        sourceText,
        sourceRefs: [sourceRef],
        extractionVersion: normalizeText(
          source.profileSuggestionSummary?.extractor?.promptVersion
          || PROFILE_SUGGESTION_EXTRACTION_VERSION
        ),
        confidence,
        capturedAt: timestamp,
        capturedBy,
        createdAt: timestamp,
        updatedAt: timestamp,
      }),
      profileField,
      factStatus: 'current',
      profileVisibilityOverride: 'pin',
      needsReview: false,
      reviewState: 'approved',
      reviewStatus: 'approved',
      reviewedAt: timestamp,
      reviewedBy: userId || null,
      metadata: {
        profileField,
        needsReview: false,
        captureFirst: {
          needsReview: false,
          reviewState: 'approved',
        },
      },
      evidenceIdentity: {
        childId: cleanChildId,
        sourceCollection: SOURCE_NOTES_COLLECTION,
        sourceId: cleanSourceId,
    sourceVersion: source.staffOriginalVersion ? (source.sourceLifecycle?.version || 'initial') : 'initial',
        sourceItemId: `${profileField}:${sourceItemIndex}`,
        knowledgeType,
        profileField,
      },
    }))
  ));
};

export const applyProfileSuggestionsToChild = async ({
  childId = '',
  sourceId = '',
  source = {},
  suggestedProfileUpdates = source.suggestedProfileUpdates || {},
  allowEmpty = false,
  deps = {},
} = {}) => {
  const cleanChildId = normalizeText(childId || source.childId);
  const cleanSourceId = normalizeText(sourceId || source.id);
  if (!cleanChildId) {
    throw new Error('Missing child profile.');
  }
  if (!cleanSourceId) {
    throw new Error('Missing Profile suggestion source.');
  }

  const firestore = deps.db || db;
  const collectionFn = deps.collection || collection;
  const docFn = deps.doc || doc;
  const createBatch = deps.writeBatch || writeBatch;
  const timestamp = deps.serverTimestamp || serverTimestamp;
  const userId = deps.userId || auth.currentUser?.uid || null;
  const now = timestamp();
  const facts = buildKnowledgeFactsFromProfileSuggestions({
    childId: cleanChildId,
    sourceId: cleanSourceId,
    sourceVersion: source.staffOriginalVersion ? (source.sourceLifecycle?.version || 'initial') : 'initial',
    source,
    suggestedProfileUpdates,
    userId,
    timestamp: now,
  });
  if (!facts.length && !allowEmpty) {
    throw new Error('No supported Profile suggestions were available to add.');
  }

  const batch = createBatch(firestore);
  const knowledgeCollection = collectionFn(firestore, KNOWLEDGE_ITEMS_COLLECTION);
  const savedItems = facts.map((fact) => {
    const { evidenceIdentity, ...factPayload } = fact;
    const factId = buildKnowledgeEvidenceDocumentId(evidenceIdentity);
    const factRef = docFn(knowledgeCollection, factId);
    batch.set(factRef, factPayload);
    return {
      id: factRef.id,
      profileField: fact.profileField,
      title: fact.title,
    };
  });
  const sourceRef = docFn(firestore, SOURCE_NOTES_COLLECTION, cleanSourceId);
  batch.update(sourceRef, {
    reviewState: 'reviewed',
    reviewLabel: 'Applied',
    profileSuggestionsAppliedAt: now,
    profileSuggestionsAppliedBy: userId,
    'profileSuggestionSummary.needsReview': false,
    'profileSuggestionSummary.savedKnowledgeItemCount': savedItems.length,
    'profileSuggestionSummary.savedKnowledgeItemIds': savedItems.map((item) => item.id),
    'metadata.careSourceProcessing.reviewState': 'reviewed',
    'metadata.careSourceProcessing.reviewLabel': 'Applied',
  });

  await batch.commit();
  return { savedItems };
};
